import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
  ReloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  ConfigProvider,
  Form,
  Input,
  InputNumber,
  Modal as AntModal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import frFR from "antd/locale/fr_FR";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";

const ENDPOINT = "/api/document-a-reclamer";

/**
 * Catalogue paramétrable des documents à réclamer sur l'attestation de prise en charge
 * (bloc « Vous voudrez bien nous adresser : … »). Liste plate : intitulé, ordre, « par défaut ».
 */
const DocumentsAReclamer = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchAll = () => {
    setLoading(true);
    axiosInstance
      .get(ENDPOINT)
      .then((res) => setDocuments(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error("Erreur lors du chargement des documents"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const open = (d = null) => {
    setEditing(d);
    form.setFieldsValue(
      d
        ? { intitule: d.intitule, ordre: d.ordre, parDefaut: !!d.parDefaut }
        : { intitule: "", ordre: documents.length + 1, parDefaut: false }
    );
    setModal(true);
  };

  const submit = async (values) => {
    setSaving(true);
    try {
      const payload = { ...values, codeId: editing?.codeId };
      if (editing) await axiosInstance.put(ENDPOINT, payload);
      else await axiosInstance.post(ENDPOINT, payload);
      toast.success(editing ? "Document modifié" : "Document ajouté");
      setModal(false);
      fetchAll();
    } catch (_) {
      toast.error("Échec de l'enregistrement du document");
    } finally {
      setSaving(false);
    }
  };

  const remove = (d) => {
    AntModal.confirm({
      title: "Supprimer ce document ?",
      icon: <ExclamationCircleFilled style={{ color: "#fa541c" }} />,
      content: d.intitule,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () =>
        axiosInstance
          .delete(`${ENDPOINT}/${d.codeId}`)
          .then(() => { toast.success("Document supprimé"); fetchAll(); })
          .catch(() => { toast.error("Échec de la suppression"); throw new Error(); }),
    });
  };

  const columns = [
    { title: "Document", dataIndex: "intitule", key: "intitule" },
    { title: "Ordre", dataIndex: "ordre", key: "ordre", width: 90, align: "center" },
    {
      title: "Coché par défaut", dataIndex: "parDefaut", key: "parDefaut", width: 150, align: "center",
      render: (v) => (v ? <Tag color="green">Par défaut</Tag> : "—"),
    },
    {
      title: "Actions", key: "a", width: 110, align: "center", render: (_, d) => (
        <Space>
          <Tooltip title="Modifier"><Button type="link" icon={<EditOutlined />} onClick={() => open(d)} /></Tooltip>
          <Tooltip title="Supprimer"><Button type="link" danger icon={<DeleteOutlined />} onClick={() => remove(d)} /></Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <style>{`
        .dar-header { background: linear-gradient(135deg, #242166 0%, #3b3b8f 100%); border-radius: 16px; padding: 26px 32px; margin-bottom: 24px; color: #fff; box-shadow: 0 4px 20px rgba(36,33,102,0.25); }
        .dar-header h2 { color: #fff; font-size: 24px; font-weight: 700; margin: 0 0 6px 0; }
        .dar-header p { color: rgba(255,255,255,.85); margin: 0; }
      `}</style>
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper" style={{ background: "#f5f5f5" }}>
            <div className="container-xxl flex-grow-1 container-p-y">
              <div className="dar-header">
                <h2><FileTextOutlined style={{ marginRight: 10 }} />Documents à réclamer (prise en charge)</h2>
                <p>Catalogue paramétrable du bloc « Vous voudrez bien nous adresser : … » de l'attestation de prise en charge</p>
              </div>

              <ConfigProvider locale={frFR}>
                <Card
                  style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                  title={<Space><FileTextOutlined /> Documents</Space>}
                  extra={
                    <Space>
                      <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>Actualiser</Button>
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => open(null)} style={{ background: "#242166" }}>Ajouter un document</Button>
                    </Space>
                  }
                >
                  <Table loading={loading} columns={columns} dataSource={documents} rowKey="codeId" pagination={{ pageSize: 15, showSizeChanger: true }} size="small" />
                </Card>
              </ConfigProvider>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AntModal
        title={editing ? "Modifier le document" : "Ajouter un document"}
        open={modal}
        onCancel={() => setModal(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={submit} requiredMark="optional">
          <Form.Item label="Document à réclamer" name="intitule" rules={[{ required: true, message: "Intitulé requis" }]}>
            <Input.TextArea rows={2} placeholder="Ex : Un rapport médical sous pli confidentiel à l'attention de notre médecin conseil" />
          </Form.Item>
          <Form.Item label="Ordre" name="ordre"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
          <Form.Item label="Coché par défaut à l'ouverture ?" name="parDefaut" valuePropName="checked" extra="Les documents « par défaut » sont pré-cochés dans la modale de sélection.">
            <Switch checkedChildren="Oui" unCheckedChildren="Non" />
          </Form.Item>
        </Form>
      </AntModal>
    </div>
  );
};

export default DocumentsAReclamer;
